import { Inject, Injectable } from "@angular/core";
import { Diary } from "../models/diary";
import { catchError, distinctUntilChanged, filter, NotFoundError, Observable, of } from "rxjs";
import { HubConnection } from "@microsoft/signalr";
import { Store } from "@ngxs/store";
import { UserState } from "../state/user/user-state";
import { SignalingService } from "./hub.service";
import { Diaries } from "../state/diaries/diary-actions";
import { ApiService } from "../services/api.service";
import { MessageService } from "primeng/api";
import { formatDate } from "@angular/common";
import { BASE_URL, DIARY_HUB_URL } from "./injection-tokens";
import { User } from "../models/user";

export abstract class DiaryServiceBase
{
    abstract getMyDiaries(includeClosed:boolean) : Observable<Diary[]>;
    abstract getMyOpenDiaries(from?:Date, to?:Date) : Observable<Diary[]>;
    abstract getMyClosedDiaries(from?: Date, to?:Date) : Observable<Diary[]>;
    abstract getDiary(diaryId: number) : Observable<Diary | undefined>;
}

@Injectable({providedIn: 'root'})
export class DiaryService extends DiaryServiceBase
{


    currentUser$ = this._store.select(UserState.currentUser);
    private _diaryHub: HubConnection;
    
    constructor
    (
        private _store:Store,
        private _signaling: SignalingService,
        private _api: ApiService,
        private _messages: MessageService,
        @Inject(DIARY_HUB_URL) private _diaryHubUrl: string,
        @Inject(BASE_URL) private _baseUrl: string,

    )
    {
        super();
        
        this._diaryHub = this._signaling.generateHub(this._diaryHubUrl);
        
        this.currentUser$
            .pipe(
                filter((cu): cu is User => cu != null && cu.id != null),
                distinctUntilChanged((a, b) => a.id === b.id)
            )
            .subscribe( cu => {
                if (cu?.id != null) {
                    this._signaling.startHub(this._diaryHub);
                } else {
                    this._diaryHub.stop().catch(err => console.error('Hub stop failed', err));
                }
            });
        this._diaryHub.on("DiaryAssigned", (diaryId: number, userId: number) => this._store.dispatch(new Diaries.DiaryServiceDiaryAssigned({ diaryId: diaryId, userId: userId })));

    }

    override getMyDiaries(includeClosed: boolean = false): Observable<Diary[]> 
    {
        return this._api.makeAPIGetCall<Diary[]>(this._baseUrl,"/diaries/mine",
        {
            ... includeClosed && {includeClosed: includeClosed.toString()}
        }
        )
        .pipe
        (
            catchError( (error:NotFoundError) => {return []} )
        );
    }

    override getMyOpenDiaries(from?: Date, to?: Date): Observable<Diary[]> 
    {
        return this._api.makeAPIGetCall<Diary[]>(this._baseUrl,"/diaries/mine/open/search",
            {
                ... from && {dueFrom: formatDate(from, "MM/dd/yyyy","en-us")},
                ... to && {dueTo: formatDate(to, "MM/dd/yyyy","en-us")},
            }
            )
            .pipe
            (
                catchError( (error:NotFoundError) => {return []} )
            );    
    }
    override getMyClosedDiaries(from?: Date, to?: Date): Observable<Diary[]> 
    {
        return this._api.makeAPIGetCall<Diary[]>(this._baseUrl,"/diaries/mine/closed/search",
            {
                ... from && {closedFrom: formatDate(from, "MM/dd/yyyy","en-us")},
                ... to && {closedTo: formatDate(to, "MM/dd/yyyy","en-us")},
            }
            )
            .pipe
            (
                catchError( (error:NotFoundError) => {return []} )
            );    
    }    

    override getDiary(diaryId: number) : Observable<Diary | undefined>
    {
        return this._api.makeAPIGetCall<Diary>(this._baseUrl,"/diaries/"+diaryId.toString())
        .pipe
        (
            catchError( (error:NotFoundError) => {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Get Diary', detail: ' ' + error.message}); return of(undefined)})
        );
    }
}